import React, { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { GlobalContext } from "../../context/globalContext";
import { isSuperAdminRole } from "../../api/admin";
import {
  getAdminPaperVideoDownloadUrl,
  listAdminPaperVideos,
  type AdminPaperVideoRow,
} from "../../api/paperVideos";
import { formatFileSize } from "../PaperVideoPage/formatFileSize";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";

export default function UserPagePaperVideos() {
  const { currentUser } = useContext(GlobalContext);

  const [videos, setVideos] = useState<AdminPaperVideoRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const limit = 25;

  const isAdmin = currentUser && isSuperAdminRole(currentUser.role);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAdminPaperVideos({ page, limit, search: search || undefined })
      .then((result) => {
        if (cancelled) return;
        setVideos(result.videos);
        setTotal(result.total);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(getUserFacingErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, page, search]);

  if (!currentUser) {
    return <p>Please sign in.</p>;
  }

  if (!isAdmin) {
    return <Navigate to="/user/events" replace />;
  }

  async function handleDownload(id: number) {
    setDownloadingId(id);
    try {
      const { downloadUrl } = await getAdminPaperVideoDownloadUrl(id);
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(getUserFacingErrorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <h2 className="font-bold text-lg text-slate-900 mb-1">Paper Videos</h2>
      <p className="text-sm text-slate-600 mb-6">
        All uploaded paper presentation videos, across all conferences.
        {!loading && (
          <>
            {" "}
            <span className="font-medium text-slate-800">
              {total} {total === 1 ? "paper" : "papers"} total.
            </span>
          </>
        )}
      </p>

      <input
        type="text"
        placeholder="Search by paper ID, name, or email"
        value={search}
        onChange={(e) => {
          setPage(1);
          setSearch(e.target.value);
        }}
        className="w-full max-w-sm border border-slate-300 bg-white px-3 py-2 text-sm rounded-sm mb-4 focus:outline-none focus:ring-2 focus:ring-slate-400"
      />

      {error && <ErrorAlert className="mb-4">{error}</ErrorAlert>}

      {loading ? (
        <LoadingPanel title="Loading paper videos" />
      ) : videos.length === 0 ? (
        <p className="text-sm text-slate-500">No paper videos found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-600">
                <th className="py-2 pr-4">Paper ID</th>
                <th className="py-2 pr-4">Paper title</th>
                <th className="py-2 pr-4">Author</th>
                <th className="py-2 pr-4">Conference</th>
                <th className="py-2 pr-4">File</th>
                <th className="py-2 pr-4">Size</th>
                <th className="py-2 pr-4">Uploaded</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {videos.map((video) => (
                <tr key={video.id} className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-medium">{video.paperId}</td>
                  <td className="py-2 pr-4">{video.paperTitle}</td>
                  <td className="py-2 pr-4">
                    <div>{video.authorName}</div>
                    <div className="text-xs text-slate-500">{video.authorEmail}</div>
                  </td>
                  <td className="py-2 pr-4">{video.conferenceLabel}</td>
                  <td className="py-2 pr-4">{video.originalFileName}</td>
                  <td className="py-2 pr-4">{formatFileSize(video.fileSizeBytes)}</td>
                  <td className="py-2 pr-4">
                    {new Date(video.uploadedAt).toLocaleDateString()}
                  </td>
                  <td className="py-2 pr-4">
                    <button
                      type="button"
                      className="eh-btn-secondary px-3 py-1.5 text-xs"
                      disabled={downloadingId === video.id}
                      onClick={() => handleDownload(video.id)}
                    >
                      {downloadingId === video.id ? "Preparing…" : "Download"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex items-center gap-3 mt-4 text-sm">
              <button
                type="button"
                className="eh-btn-secondary px-3 py-1.5 text-xs disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="text-slate-600">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="eh-btn-secondary px-3 py-1.5 text-xs disabled:opacity-50"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
